import { useState, useRef, useCallback, useEffect, type ReactNode } from 'react';
import { Box, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';
import { useDeleteContributorRoleSlotMutation } from '@/state/mutations/contributors/use-delete-contributor-role-slot';
import { useNotifications } from '@/hooks/use-notifications';
import { useTranslations } from '@/i18n/use-translations';
import { useDeleteEntityContributor } from '@/hooks/use-delete-entity-contributor';
import { useBreakpointDown } from '@/hooks/use-breakpoint';

type SwipeableContributorItemProps = {
  contributor: ContributorDto;
  children: ReactNode;
  sessionId?: string;
  flow?: FlowDto;
  flowSlotId?: string;
  roleId?: string;
  roleslotId?: string;
  sessionRoleId?: string; // Role ID to delete when removing from session
  session?: SessionDto; // Session object for flow context (to check isInLibrary)
};

const SWIPE_THRESHOLD = 80; // Minimum swipe distance to trigger showing the action
const ACTION_WIDTH = 80; // Visible action area width (icon lives here)
const BOUNCE_RESISTANCE = 0.2; // Resistance factor when swiping past boundaries (0-1, lower = more resistance/elasticity)
const BOUNCE_EASING = 'cubic-bezier(0.25, 1.7, 0.35, 0.8)'; // More pronounced spring-like bounce easing
const MAX_OVERSHOOT = 50; // Maximum pixels to allow overshooting past boundaries (increased for more elastic feel)

export default function SwipeableContributorItem({
  contributor,
  children,
  sessionId,
  flow,
  roleId,
  roleslotId,
  sessionRoleId,
  session,
}: SwipeableContributorItemProps) {
  const isMobile = useBreakpointDown('sm');
  const t = useTranslations();
  const notification = useNotifications();

  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const lastTouchX = useRef<number | null>(null);
  const swipeOffsetRef = useRef(0);
  const [isActionPending, setIsActionPending] = useState(false);
  const ignoreTouchUntilRef = useRef<number>(0);
  const modalTransitionUntilRef = useRef<number>(0);
  const initialSwipeOffsetRef = useRef(0);

  const setSwipeOffsetSynced = useCallback((value: number) => {
    swipeOffsetRef.current = value;
    setSwipeOffset(value);
    // Update initial offset ref when resetting to ensure it matches current position
    if (value === 0 || value === -ACTION_WIDTH) {
      initialSwipeOffsetRef.current = value;
    }
  }, []);

  const blockSwipeFor = useCallback((ms: number) => {
    const until = Date.now() + ms;
    modalTransitionUntilRef.current = until;
    ignoreTouchUntilRef.current = Math.max(ignoreTouchUntilRef.current, until);
  }, []);

  const shouldIgnoreTouches = useCallback(() => {
    return Date.now() < ignoreTouchUntilRef.current || Date.now() < modalTransitionUntilRef.current;
  }, []);

  const { mutateAsync: deleteContributorRoleSlot } = useDeleteContributorRoleSlotMutation();
  const deleteEntityContributor = useDeleteEntityContributor();

  // Determine action type (explicit by presence of ids)
  // - roleslot removal: requires roleId + roleslotId (flow-slot context)
  // - session removal: requires sessionId
  // - flow removal: only if flow is provided and not the above two
  const isRemovingFromSlot = !!roleId && !!roleslotId;
  const isRemovingFromSession = !!sessionId && !isRemovingFromSlot;
  const isRemovingFromFlow = !!flow && !isRemovingFromSlot && !isRemovingFromSession;

  const displayName =
    contributor.profiles?.[0]?.displayName || contributor.email || t('event.detail.contributors.unknownContributor');

  const hasSwipeAction = isRemovingFromSlot || isRemovingFromSession || isRemovingFromFlow;

  const isInteractionBlocked = isActionPending;
  const effectiveSwipeOffset = isInteractionBlocked ? 0 : swipeOffset;

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!isMobile || shouldIgnoreTouches() || isInteractionBlocked) return;
      const touch = e.touches[0];
      touchStartX.current = touch.clientX;
      touchStartY.current = touch.clientY;
      lastTouchX.current = touch.clientX;
      // Store initial offset from state (matches visual position) to determine swipe direction
      initialSwipeOffsetRef.current = swipeOffsetRef.current;
      setIsSwiping(false);
    },
    [isMobile, isInteractionBlocked, shouldIgnoreTouches],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isMobile || shouldIgnoreTouches() || isInteractionBlocked) return;
      if (touchStartX.current === null || touchStartY.current === null) return;
      if (lastTouchX.current === null) return;

      const touch = e.touches[0];
      const totalDeltaX = touch.clientX - touchStartX.current;
      const totalDeltaY = touch.clientY - touchStartY.current;

      // Only allow horizontal swiping (prevent vertical scrolling interference)
      if (Math.abs(totalDeltaX) > Math.abs(totalDeltaY) && Math.abs(totalDeltaX) > 10) {
        e.preventDefault();
        e.stopPropagation();

        // Capture the initial offset at the moment we *commit* to a horizontal swipe.
        // This makes swipe-direction detection robust even if touchstart was missed/blocked.
        if (!isSwiping) {
          initialSwipeOffsetRef.current = swipeOffsetRef.current;
        }

        setIsSwiping(true);

        // Incremental delta since last move (prevents double-counting)
        const dx = touch.clientX - lastTouchX.current;
        lastTouchX.current = touch.clientX;

        let next = swipeOffsetRef.current + dx;

        // Add elastic resistance when swiping past boundaries (rubber-band effect)
        if (next > 0) {
          // Swiping right past closed position - apply strong elastic resistance
          next = next * BOUNCE_RESISTANCE;
          // Cap the overshoot
          if (next > MAX_OVERSHOOT) {
            next = MAX_OVERSHOOT;
          }
        } else if (next < -ACTION_WIDTH) {
          // Swiping left past open position - apply strong elastic resistance with bounce
          const overshoot = next + ACTION_WIDTH;
          const elasticOvershoot = overshoot * BOUNCE_RESISTANCE;
          next = -ACTION_WIDTH + elasticOvershoot;
          // Cap the overshoot
          if (next < -ACTION_WIDTH - MAX_OVERSHOOT) {
            next = -ACTION_WIDTH - MAX_OVERSHOOT;
          }
        }

        setSwipeOffsetSynced(next);
      }
    },
    [isMobile, isInteractionBlocked, shouldIgnoreTouches, setSwipeOffsetSynced, isSwiping],
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!isMobile) return;

      // If we're ignoring touches (e.g., just confirmed/canceled), force-close.
      if (shouldIgnoreTouches() || isInteractionBlocked) {
        setSwipeOffsetSynced(0);
        setIsSwiping(false);
        touchStartX.current = null;
        touchStartY.current = null;
        lastTouchX.current = null;
        return;
      }

      // Use ref value for current offset (most up-to-date during swipe)
      const currentOffset = swipeOffsetRef.current;
      const initialOffset = initialSwipeOffsetRef.current;
      const swipeDirection = currentOffset - initialOffset; // positive = swiping right (closing), negative = swiping left (opening)

      // If we were swiping (have an offset), prevent drag-and-drop
      if (currentOffset !== 0) {
        e.stopPropagation();
      }

      setIsSwiping(false);

      // Snap behavior with bounce (CSS easing handles the bounce animation):
      // - If swiping right (closing) from an open position, snap closed
      // - If swiping left (opening), snap open if moved enough
      const isSwipingRight = swipeDirection > 0;
      const isSwipingLeft = swipeDirection < 0;

      if (isSwipingRight && currentOffset > -ACTION_WIDTH) {
        // Swiping right (closing) - snap closed (bounce handled by CSS)
        setSwipeOffsetSynced(0);
      } else if (isSwipingLeft && currentOffset <= -(SWIPE_THRESHOLD / 2)) {
        // Swiped left enough (past -40px) - snap open (bounce handled by CSS)
        setSwipeOffsetSynced(-ACTION_WIDTH);
      } else if (currentOffset <= -(SWIPE_THRESHOLD / 2)) {
        // Already past threshold - keep it open
        setSwipeOffsetSynced(-ACTION_WIDTH);
      } else {
        // Not enough movement - snap closed (bounce handled by CSS)
        setSwipeOffsetSynced(0);
      }

      touchStartX.current = null;
      touchStartY.current = null;
      lastTouchX.current = null;
    },
    [isMobile, isInteractionBlocked, shouldIgnoreTouches, setSwipeOffsetSynced],
  );

  const handleDeleteClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      // Block touches FIRST, then reset all refs immediately
      blockSwipeFor(600);
      setSwipeOffsetSynced(0);
      setIsSwiping(false);
      touchStartX.current = null;
      touchStartY.current = null;
      lastTouchX.current = null;
      setIsActionPending(true);

      try {
        if (isRemovingFromSlot && roleId && roleslotId) {
          // For non-library sessions, use session context deletion (delete both role and roleSlot)
          if (session?.isInLibrary === false && sessionId && sessionRoleId) {
            await deleteEntityContributor({
              contributor,
              entityType: 'Session',
              entityId: sessionId,
            });
          } else {
            // For library sessions, only delete the roleSlot (existing behavior)
            await deleteContributorRoleSlot({
              contributorId: contributor.id,
              roleId,
              slotId: roleslotId,
            });
            notification.notifySuccess(
              t('event.detail.contributors.notifications.deleteRoleSlotSuccess', { name: displayName }),
            );
          }
        } else if (isRemovingFromFlow && flow && sessionRoleId) {
          // Remove contributor from flow using shared logic
          await deleteEntityContributor({
            contributor,
            entityType: 'Flow',
            entityId: flow.id,
          });
        } else if (isRemovingFromSession && sessionId && sessionRoleId) {
          // Remove contributor from session using shared logic
          await deleteEntityContributor({
            contributor,
            entityType: 'Session',
            entityId: sessionId,
          });
        }
        setSwipeOffsetSynced(0);
      } catch {
        blockSwipeFor(600);
        setSwipeOffsetSynced(0);
        notification.notifyError(
          t('event.detail.contributors.notifications.deleteRoleSlotError', { name: displayName }),
        );
      } finally {
        setIsActionPending(false);
        setSwipeOffsetSynced(0);
        setIsSwiping(false);
        touchStartX.current = null;
        touchStartY.current = null;
        lastTouchX.current = null;
      }
    },
    [
      isRemovingFromSlot,
      isRemovingFromFlow,
      isRemovingFromSession,
      roleId,
      roleslotId,
      flow,
      sessionId,
      sessionRoleId,
      session,
      contributor,
      deleteContributorRoleSlot,
      deleteEntityContributor,
      blockSwipeFor,
      setSwipeOffsetSynced,
      displayName,
      notification,
      t,
    ],
  );

  // Keep ref in sync to avoid stale closures when touch events arrive late.
  useEffect(() => {
    swipeOffsetRef.current = swipeOffset;
  }, [swipeOffset]);

  // While an action is pending, keep the *state* closed as well
  // (not only the visual effectiveSwipeOffset). This prevents any late touch events
  // from re-opening the swipe during the removal operation.
  useEffect(() => {
    if (isInteractionBlocked) {
      setSwipeOffsetSynced(0);
    }
  }, [isInteractionBlocked, setSwipeOffsetSynced]);
  // For non-library sessions in flow context, use DeleteIcon (session deletion), otherwise use RemoveCircleOutline (roleSlot removal)
  const isRemoveAction = (isRemovingFromSlot && session?.isInLibrary !== false) || isRemovingFromFlow;
  const ActionIcon = isRemoveAction ? RemoveCircleOutline : DeleteIcon;

  // Don't apply swipe functionality on desktop or when there's no action to perform
  // But still wrap in Box with margin for consistent spacing
  if (!isMobile || !hasSwipeAction) {
    return (
      <Box
        sx={{
          m: '0.4rem 0',
        }}
      >
        {children}
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          // Match the margin that CollapsibleBox typically has
          m: '0.4rem 0',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Delete/Remove button background */}
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: `${ACTION_WIDTH}px`,
            backgroundColor: 'error.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            transform: `translateX(${ACTION_WIDTH + effectiveSwipeOffset}px)`,
            transition: isSwiping ? 'none' : `transform 0.5s ${BOUNCE_EASING}`,
          }}
        >
          <IconButton
            onClick={handleDeleteClick}
            sx={{
              'color': 'white',
              '&:hover': {
                backgroundColor: 'error.dark',
              },
            }}
            aria-label={isRemoveAction ? 'remove' : 'delete'}
          >
            <ActionIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Contributor item */}
        <Box
          sx={{
            transform: `translateX(${effectiveSwipeOffset}px)`,
            transition: isSwiping ? 'none' : `transform 0.5s ${BOUNCE_EASING}`,
            position: 'relative',
            zIndex: 2,
          }}
        >
          {children}
        </Box>
      </Box>
    </>
  );
}
